// @flow
import type { SampleList, Sample, Column } from "../../lib/OnlineTypes";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";
import { fetchJson } from "../../lib/Fetching";
import SampleActionBar from "./SampleActionBar";
import SampleRow from "./SampleRow";
import SampleStatusKey from "./SampleStatusKey";
import Link from "../Link";
import moment from "moment";
import { MinimalContainer } from "./Theme";
import _, { sortBy } from "underscore";

const Container = styled(MinimalContainer)`
  width: 100%;
  display: table;
`;

type Props = {
  sample_list: SampleList,
  company_id: ?number,
  showScheduledStatus: ?boolean,
  ToolbarComponent: ?Object,
  ToolbarComponent2: ?Object,
  canSelect: ?Function,
  highlight: ?Function,
  onSampleSelectChange: ?Function,
  showAttributes: boolean,
  clientView: ?boolean,
  ExtraHeader: ?Object,
  ExtraBody: ?Object,
  reloadData: ?Function,
  showContractedTechniques: ?boolean,
  showScheduleableTechniques: ?boolean,
  loadSelectedSamples: ?Function,
  view: ?string,
  viewingSampleCharges: ?boolean,
  invoiceId: ?number,
  oneSample: ?boolean,
  toolbarOptions: ?Object,
};

type State = {
  selectedSamples: Array<Sample>,
  selectAllChecked: boolean,
  sample_list: SampleList,
  showAttributes: boolean,
  sortByDueDate: boolean,
  groupByCompany: boolean,
  dateGroup: string,
  // groupByRegisteredDate: boolean
};

const HeadTr = styled.tr`
  > th {
    text-align: center;
    font-size: 8pt;
  }
`;

function compareSampleDates(s1, s2) {
  if (!s1.due_date && !s2.due_date) return 0;
  if (!s1.due_date) return 1;
  if (!s2.due_date) return -1;
  var a = moment.utc(s1.due_date);
  var b = moment.utc(s2.due_date);
  if (a.isBefore(b)) {
    return -1;
  }
  if (a.isAfter(b)) {
    return 1;
  }
  // a must be equal to b
  return 0;
}

function compareSampleIdentifiers(s1, s2) {
  return s1.identifier - s2.identifier;
}

export default class Samples extends React.Component<Props, State> {
  static defaultProps = {
    showScheduledStatus: false,
    ToolbarComponent: SampleActionBar,
    ToolbarComponent2: null,
    canSelect: () => true,
    clientView: true,
    showContractedTechniques: false,
    showScheduleableTechniques: false,
    view: "all",
    viewingSampleCharges: false,
    invoiceId: null,
    showGroupBy: true,
    oneSample: false,
    toolbarOptions: {},
  };

  state = {
    selectedSamples: [],
    selectAllChecked: false,
    sample_list: this.props.sample_list,
    showAttributes: this.props.showAttributes === false ? false : true,
    sortByDueDate: this.props.sortByDueDate || false,
    groupByCompany: false,
    dateGroup: "none",
    nowrapProduct: false,
  };

  dueDateSort = () => this.setState({ sortByDueDate: !this.state.sortByDueDate });

  // All samples are reloaded.
  // The sampleIds parameter is not currently used.
  reloadSamples = (sampleIds: Array<number>) => {
    const { sample_list } = this.state;
    const sample_ids = sample_list.samples.map((s) => s.identifier);
    fetchJson("/lims_sample_reporting/samples.json", {
      form: {
        sample_ids,
        with_unallocated_sample_ledger_items: viewingSampleCharges,
      },
    })
      .then((json) => {
        this.setState({ sample_list: json.sample_list });
      })
      .catch((e) => {
        alert("Problem loading samples.");
      });
  };

  toggleSelectAll = () => {
    const { selectAllChecked } = this.state;
    if (selectAllChecked) {
      this.setState(
        { selectAllChecked: false, selectedSamples: [] },
        this.onSampleSelectChange
      );
    } else {
      this.setState(
        { selectAllChecked: true, selectedSamples: this.allSamples() },
        this.onSampleSelectChange
      );
    }
  };

  allSamples = () => this.state.sample_list.samples.filter((s) => this.canSelect(s));

  selectSample = (sample: Sample) => {
    const { selectedSamples } = this.state;
    if (this.isSelected(sample)) {
      this.setState(
        {
          selectedSamples: selectedSamples.filter(
            (s) => this.sampleIdToUse(s) !== this.sampleIdToUse(sample)
          ),
        },
        this.onSampleSelectChange
      );
    } else {
      this.setState(
        { selectedSamples: selectedSamples.concat(sample) },
        this.onSampleSelectChange
      );
    }
  };

  selectSamples = (samples: Array<Sample>, checked: boolean) => {
    const { selectedSamples } = this.state;
    const identifiers = samples.map(this.sampleIdToUse);
    if (!checked) {
      this.setState(
        {
          selectedSamples: selectedSamples.filter(
            (s) => !identifiers.includes(this.sampleIdToUse(s))
          ),
        },
        this.onSampleSelectChange
      );
    } else {
      this.setState(
        {
          selectedSamples: selectedSamples.concat(samples.filter((s) => !this.isSelected(s))),
        },
        this.onSampleSelectChange
      );
    }
  };

  onSampleSelectChange = () => {
    this.props.onSampleSelectChange &&
      this.props.onSampleSelectChange(
        this.state.selectedSamples.map((s) => this.sampleIdToUse(s))
      );
    this.props.onSampleChargeSelectChange &&
      this.props.onSampleChargeSelectChange(
        this.state.selectedSamples.map((s) => s.ledger_item_id)
      );
  };

  sampleIdToUse = (sample) =>
    this.props.viewingSampleCharges ? sample.ledger_item_id : sample.identifier;

  isSelected = (sample: Sample) =>
    this.state.selectedSamples
      .map((s) => this.sampleIdToUse(s))
      .includes(this.sampleIdToUse(sample));

  canSelect = (sample: Sample) => this.props.canSelect(sample);

  render() {
    if (!this.state.sample_list) return null;

    // Used by action bar
    const {
      company_id,
      ToolbarComponent,
      ToolbarComponent2,
      highlight,
      clientView,
      ExtraHeader,
      ExtraBody,
      reloadData,
      showContractedTechniques,
      showScheduleableTechniques,
      view,
      loadSelectedSamples,
      viewingSampleCharges,
      invoiceId,
      showGroupBy,
      oneSample,
      toolbarOptions,
    } = this.props;
    const {
      selectedSamples,
      showAttributes,
      groupByCompany,
      // groupByRegisteredDate,
      dateGroup,
      nowrapProduct,
    } = this.state;
    const selectedSampleIds = selectedSamples.map((s) => s.identifier);

    // Table vars
    const { showScheduledStatus } = this.props;
    const {
      sample_list: { columns, samples, techniques },
      sortByDueDate,
    } = this.state;
    const { selectAllChecked } = this.state;
    var sortedColumns = columns.sort((a, b) => a.position - b.position);
    if (!showAttributes) {
      sortedColumns = columns.filter((c) => c.always_show);
    }

    const sortedTechniques = _.sortBy(techniques, (t) => t.name);

    const showCompanyName = clientView ? false : samples.some((s) => s.company_name);
    const showCompanyBranchName = samples.some((s) => s.company_branch_name);
    const showLaboratorySampleStatus = samples.some((s) => s.laboratory_sample);
    const sortedSamples = sortByDueDate
      ? samples.sort(compareSampleDates)
      : samples.sort(compareSampleIdentifiers);

    const companyGroupFunc = (s) => s.company_name;
    const registeredDateFunc = (s) =>
      "[" +
      moment
        .utc(s[dateGroup])
        .local()
        .format("YYYY-MM-DD") +
      "] " +
      (s[dateGroup]
        ? moment
            .utc(s[dateGroup])
            .local()
            .format("ddd DD MMM")
        : "None");
    var groupFuncs = [];
    if (dateGroup !== "none") groupFuncs.push(registeredDateFunc);
    if (groupByCompany) groupFuncs.push(companyGroupFunc);

    var grouped;
    if (groupFuncs.length > 0) {
      grouped = _.groupBy(sortedSamples, (s) => groupFuncs.map((f) => f(s)).join(" - "));
    }

    var totalPrice = 0;
    sortedSamples.map((s) => (totalPrice += Number(s.ledger_item_price)));
    totalPrice = Math.round(totalPrice * 100) / 100;

    return (
      <Container>
        <div className="samples-header" style={{ padding: oneSample ? 5 : 5 }}>
          {!oneSample && (
            <label style={{ color: "#888", marginBottom: 0 }}>{samples.length} samples</label>
          )}
          <label style={{ marginLeft: 20, marginBottom: 0 }}>
            <input
              onChange={() => this.setState({ showAttributes: !showAttributes })}
              type="checkbox"
              checked={showAttributes}
            />
            &nbsp; {I18n.t("show_additional_sample_attributes")}
          </label>
          {viewingSampleCharges && (
            <label style={{ marginLeft: 50 }}>
              Total Price Charged: &nbsp;
              {totalPrice}
            </label>
          )}
          {!clientView &&
            showGroupBy && (
              <label style={{ marginLeft: 20, marginBottom: 0 }}>
                <input
                  onChange={() => this.setState({ groupByCompany: !groupByCompany })}
                  type="checkbox"
                  checked={groupByCompany}
                />
                &nbsp; {I18n.t("group_by_company")}
              </label>
            )}
          {!clientView &&
            showGroupBy && (
              <Fragment>
                <span style={{ marginLeft: 20 }}>Group By:</span>
                <label style={{ marginLeft: 20, marginBottom: 0 }}>
                  <input
                    onChange={(e) => this.setState({ dateGroup: e.target.value })}
                    name="groupBy"
                    type="radio"
                    value="registered_at"
                    checked={dateGroup == "registered_at"}
                  />
                  &nbsp; {I18n.t("date_registered")}
                </label>
                <label style={{ marginLeft: 20, marginBottom: 0 }}>
                  <input
                    onChange={(e) => this.setState({ dateGroup: e.target.value })}
                    name="groupBy"
                    type="radio"
                    value="requested_test_date"
                    checked={dateGroup == "requested_test_date"}
                  />
                  &nbsp; {I18n.t("requested_test_date")}
                </label>
                <label style={{ marginLeft: 20, marginBottom: 0 }}>
                  <input
                    onChange={(e) => this.setState({ dateGroup: e.target.value })}
                    name="groupBy"
                    type="radio"
                    value="contract_reviewed_at"
                    checked={dateGroup == "contract_reviewed_at"}
                  />
                  &nbsp; {I18n.t("contract_review_date")}
                </label>
              </Fragment>
            )}
        </div>
        {ToolbarComponent2 && (
          <ToolbarComponent2
            sampleIds={selectedSampleIds}
            selectedSamples={selectedSamples}
            company_id={company_id}
            onSampleUpdate={this.reloadSamples}
            clientView={clientView}
            reloadData={reloadData}
            loadSelectedSamples={() => loadSelectedSamples(selectedSampleIds)}
          />
        )}
        {ToolbarComponent && (
          <ToolbarComponent
            sampleIds={selectedSampleIds}
            selectedSamples={selectedSamples}
            company_id={company_id}
            onSampleUpdate={this.reloadSamples}
            clientView={clientView}
            reloadData={reloadData}
            view={view}
            toolbarOptions={toolbarOptions}
          />
        )}
        <table className="table table-condensed table-hover" style={{ marginBottom: 0 }}>
          <thead>
            <HeadTr>
              <th style={{ width: 30 }}>
                <input
                  type="checkbox"
                  onChange={this.toggleSelectAll}
                  checked={selectAllChecked}
                />
              </th>
              <th style={{ width: 90 }}>
                {viewingSampleCharges ? "Charge Type" : I18n.t("status")}
              </th>
              <th style={{ maxWidth: 100 }}>
                <span
                  className="fa fa-comment"
                  style={{ fontSize: 14, cursor: "pointer", marginRight: 3 }}
                  title="Sample Comments"
                />
                <span
                  className="fa fa-check-square"
                  style={{ fontSize: 14, cursor: "pointer" }}
                  title="Todos"
                />
              </th>
              {showScheduledStatus && <th>{I18n.t("scheduled")}</th>}
              {ExtraHeader && <ExtraHeader />}
              {!groupByCompany && showCompanyName && <th>{I18n.t("company")}</th>}
              {showCompanyBranchName && <th>{I18n.t("branch")}</th>}
              <th>{I18n.t("sample_id")}</th>
              <th>{I18n.t("requested_test_date")}</th>
              <th>{I18n.t("requested_lab_turnaround")}</th>
              <th>
                <Link onClick={this.dueDateSort}>{I18n.t("due_date")}</Link>
              </th>
              <th>{I18n.t("results")}</th>
              <th>{I18n.t("reports")}</th>
              <th>
                <Link onClick={() => this.setState({ nowrapProduct: !nowrapProduct })}>
                  {I18n.t("product")}
                  &nbsp;&nbsp;
                  <i className={nowrapProduct ? "fa fa-expand" : "fa fa-compress"} />
                </Link>
              </th>
              <th>{I18n.t("registered")}</th>
              {showLaboratorySampleStatus && (
                <Fragment>
                  <th>
                    <b>{I18n.t("received")}</b>
                  </th>
                  <th>
                    <b>{I18n.t("sent")}</b>
                  </th>
                </Fragment>
              )}
              {!showLaboratorySampleStatus && <th>{I18n.t("received")}</th>}
              <th>{I18n.t("suite")}</th>
              <th>{I18n.t("specification")}</th>
              {sortedColumns.map((column, i) => (
                <th key={i}>{column.name}</th>
              ))}
              {(showContractedTechniques || showScheduleableTechniques) &&
                sortedTechniques.map((t) => <th key={t.id}>{t.name}</th>)}
              <th>{viewingSampleCharges ? "Charge Amount" : I18n.t("price")}</th>
            </HeadTr>
          </thead>
          <tbody>
            {groupFuncs.length == 0 &&
              sortedSamples.map((sample, i) => (
                <SampleRow
                  key={`${sample.identifier}-${i}`}
                  sample={sample}
                  highlight={highlight}
                  selected={this.isSelected(sample)}
                  canSelect={this.canSelect(sample)}
                  selectSample={() => this.selectSample(sample)}
                  showLaboratorySampleStatus={showLaboratorySampleStatus}
                  columns={sortedColumns}
                  showScheduledStatus={showScheduledStatus}
                  showCompany={showCompanyName}
                  showBranch={showCompanyBranchName}
                  showAttributes={true}
                  clientView={clientView}
                  ExtraCols={ExtraBody}
                  showContractedTechniques={showContractedTechniques}
                  showScheduleableTechniques={showScheduleableTechniques}
                  techniques={sortedTechniques}
                  reloadSamples={reloadData}
                  viewingSampleCharge={viewingSampleCharges}
                  invoiceId={invoiceId}
                  expandSample={oneSample}
                  nowrapProduct={nowrapProduct}
                />
              ))}
            {groupFuncs.length > 0 && (
              <Fragment>
                {Object.keys(grouped).map((company) => {
                  const groupedSamples = grouped[company];
                  const label = company.replace(/\[.*\]\s/, "");
                  return (
                    <Fragment>
                      <tr style={{ backgroundColor: "#FAFAFA" }}>
                        <td
                          colSpan={14 + (techniques ? techniques.length : 0) + columns.length}
                        >
                          <label
                            style={{
                              textTransform: "uppercase",
                              marginBottom: 0,
                              marginLeft: 5,
                            }}
                          >
                            <input
                              type="checkbox"
                              defaultChecked={false}
                              onClick={(e) =>
                                this.selectSamples(groupedSamples, e.target.checked)
                              }
                            />
                            &nbsp;&nbsp;
                            {label}
                          </label>
                        </td>
                      </tr>
                      {groupedSamples.map((sample, i) => (
                        <SampleRow
                          key={i}
                          sample={sample}
                          highlight={highlight}
                          selected={this.isSelected(sample)}
                          canSelect={this.canSelect(sample)}
                          selectSample={() => this.selectSample(sample)}
                          showLaboratorySampleStatus={showLaboratorySampleStatus}
                          columns={sortedColumns}
                          showScheduledStatus={showScheduledStatus}
                          showCompany={!groupByCompany}
                          showBranch={showCompanyBranchName}
                          showAttributes={true}
                          clientView={clientView}
                          ExtraCols={ExtraBody}
                          showContractedTechniques={showContractedTechniques}
                          showScheduleableTechniques={showScheduleableTechniques}
                          techniques={sortedTechniques}
                          reloadSamples={reloadData}
                          expandSample={oneSample}
                          nowrapProduct={nowrapProduct}
                        />
                      ))}
                    </Fragment>
                  );
                })}
              </Fragment>
            )}
          </tbody>
        </table>
      </Container>
    );
  }
}
